import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Swiper, SwiperSlide } from 'swiper/react'
import SwiperClass from 'swiper/types/swiper-class'
import SwiperCore, { Navigation, Mousewheel, A11y, Keyboard } from 'swiper'
import { ApiQuery, findApiV4Link, useJsonApi } from 'shared'
import { Modal } from 'react-overlays'
import { PictureArray, PictureProps, PicturesApiResult } from './types'

SwiperCore.use([Navigation, Mousewheel, A11y, Keyboard])

/**
 * @func Gallery
 * @param {{apiUrl: string}} props
 * @returns  {JSX.Element | null}
 */
export const Gallery = ({ apiUrl }: { apiUrl: string }): JSX.Element | null => {
  const [isOpen, setIsOpen] = useState(window.location.hash === '#gallery')
  const [page, setPage] = useState(1)
  const [pictures, setPictures] = useState<PictureArray>([])
  const swiperRef = useRef<SwiperClass | null>(null)

  const updateSwiperRef = useCallback((swiper: SwiperClass) => {
    swiperRef.current = swiper
  }, [])
  const open = useCallback((index = 0) => {
    setIsOpen(true)
    if (index) swiperRef.current && swiperRef.current.slideTo(index)
  }, [])
  const close = useCallback(() => setIsOpen(false), [])

  /**
   * Expose open and close functions for interactions from SLIM
   * TODO: try to remove this absolute travesty of a side-effect, & swear solemnly to never-ever do anything like this again ;-)
   */
  useEffect(() => {
    window.openGallery = open
    window.closeGallery = close
  }, [open, close])

  const query = new ApiQuery(apiUrl)
  const currentPageUrl = query.per(3).page(page).toUrl()
  const { data, loading } = useJsonApi<PicturesApiResult>(currentPageUrl)

  // Whenever new pictures have been loaded, add them to the stack
  useEffect(() => {
    loading || (data?.data && setPictures((pics) => pics.concat(data.data)))
  }, [loading, data?.data])

  // Swiper ignores updates to onReachEnd, so use a ref.
  const pageRef = useRef(1)
  const onReachEnd = useCallback(() => setPage((pageRef.current += 1)), [])

  if (!pictures.length) return null

  const content = pictures.map((picture, index) => (
    <SwiperSlide key={`pic-${picture.id}`}>
      <Picture {...picture} index={index} total={data?.total_entries || undefined} close={close} />
    </SwiperSlide>
  ))

  // TODO: check if the useRef provided before was actually working, it does not exist on exposed Swiper API; as of now the previous behaviour changed to onSwiper cb
  return (
    <Modal
      aria-labelledby="modal-label"
      show={isOpen}
      onHide={close}
      className="projects-show-gallery-modal"
      renderBackdrop={Backdrop}
    >
      <div className="gallery-content">
        <Swiper
          slidesPerView={1}
          navigation={{
            nextEl: '#gallery-button-next',
            prevEl: '#gallery-button-prev',
            disabledClass: 'disabled',
          }}
          scrollbar={{ hide: false }}
          keyboard
          centeredSlides
          roundLengths
          mousewheel={{ forceToAxis: true }}
          onSwiper={updateSwiperRef}
          onReachEnd={onReachEnd}
        >
          {content}
        </Swiper>
        <div id="gallery-button-prev"></div>
        <div id="gallery-button-next"></div>
        <div id="gallery-button-close" onClick={close}></div>
      </div>
    </Modal>
  )
}

const Backdrop = () => <div className="projects-show-gallery-modal-backdrop"></div>

const Picture = ({ close, index, total, ...picture }: PictureProps): JSX.Element => {
  const srcUrl = findApiV4Link({ ofObject: picture, withRel: 'image' })
  const handleClick: React.MouseEventHandler<HTMLDivElement> = useCallback(
    (event) => {
      if ((event.target as HTMLDivElement).className === 'fake-backdrop') {
        event.preventDefault()
        close(event)
      }
    },
    [close]
  )

  return (
    <div className="swiper-slide text-center">
      <div className="fake-backdrop" onClick={handleClick}>
        <img src={srcUrl} className="gallery-image" />
        <div className="gallery-description pt-4">
          <p>{picture.description}</p>
          {index + 1} / {total}
        </div>
      </div>
    </div>
  )
}
